import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Breakpoint, Button, FieldErrorMessage, Input, PageTitleField, tokens } from '@treams/treams-ui';

const LearningEventHeaderBackdropContainer = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	z-index: 0;
	background-color: ${tokens.surface.sidebar};
`;

const LearningEventHeaderBackdrop = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const LearningEventHeaderContent = styled(Flex)`
	position: relative;
	z-index: 1;
	flex-grow: 1;
`;

const LearningEventHeaderElement = styled(Flex)`
	position: relative;
	margin: -16px;
	margin-bottom: 0;
	min-height: 200px;
	@media screen and (min-width: ${Breakpoint.Medium}) {
		min-height: 300px;
	}
`;

const LearningEventCreateHeaderForm = (props: {
	event?: graphql.LearningEvent;
	coverFile?: File;
	setCoverFile: (file: File) => void;
}) => {
	const { t } = useTranslation();

	const [field, , { setValue }] = useField('date');

	const ref = useRef<HTMLInputElement>(null);

	const [url, setURL] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!props.coverFile) {
			return;
		}

		const url = URL.createObjectURL(props.coverFile);
		setURL(url);

		return () => URL.revokeObjectURL(url);
	}, [props.coverFile]);

	return (
		<LearningEventHeaderElement>
			<LearningEventHeaderBackdropContainer>
				{(url || props.event?.cover?.source.url) && (
					<LearningEventHeaderBackdrop src={url ?? props.event?.cover?.source.url} />
				)}
			</LearningEventHeaderBackdropContainer>

			<LearningEventHeaderContent
				px={[2, 3, 4]}
				py={3}
				justifyContent="space-between"
				flexDirection="column"
				flexWrap={['wrap', 'nowrap', 'nowrap']}
			>
				<input
					ref={ref}
					type="file"
					style={{ display: 'none' }}
					accept="image/png, image/jpeg"
					tabIndex={-1}
					onChange={async (event) => props.setCoverFile(event.currentTarget.files![0])}
				/>
				<Flex justifyContent="flex-end">
					<Button
						small
						type="whiteTransparant"
						onClick={(event: React.MouseEvent<Element>) => {
							ref.current?.click();
						}}
						title={t('learning.event.modal.thumbnail.button.title')}
					/>
				</Flex>

				<Box mr={3}>
					<FieldErrorMessage name="title" />

					<PageTitleField
						tint="light"
						name="title"
						placeholder={t('learning.event.modal.name.placeholder')}
					/>

					<Flex mt={2}>
						<Input.DatePicker
							value={field.value}
							onChange={setValue}
							placeholder={t('learning.event.modal.date.placeholder')}
						/>
					</Flex>
				</Box>
			</LearningEventHeaderContent>
		</LearningEventHeaderElement>
	);
};

export default LearningEventCreateHeaderForm;
